// Libraries
import { MutableRefObject, useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
	GRID_CHECKBOX_SELECTION_COL_DEF,
	GridColDef,
	GridColumnHeaderParams,
	GridRowClassNameParams,
	GridRowId,
	useGridApiRef,
} from "@mui/x-data-grid-premium";
import { useSnackbar } from "notistack";
import { v4 as uuidv4 } from "uuid";

// Components
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import EditPendingRequestDialog from "../Widgets/EditPendingRequestDialog";
import { defaultCellRenderer } from "@/Layouts/components/Tables/DataSmartTable/helpers";
import TypographyCell from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import TransferAmountCell from "./DealerTrades/TransferAmountCell";
import ApproveComponent from "./DealerTrades/PendingTransferRequestTable/ApproveComponent";

// Layouts
import { SnackbarsActions, SnackbarsContent } from "@/Layouts/components/Snackbars";

// Hooks
import { useLocations } from "@/Common/hooks/Location/useLocations";
import { usePersistentSelection } from "@/Common/hooks/selection/usePersistentSelection";
import { useCommissionCyclesData } from "@/Washouts/hooks/common/useCommissionCyclesData";

// Helpers
import { getTenantParameters } from "@/Common/helpers/Tenant";
import { getFormatDate } from "@/Common/helpers/Dates";
import RowFeedback from "@/Common/helpers/RowFeedback";
import { mutateTransferRequestStatus } from "@/DataPages/api/inventory/mutations";
import {
	actionEditAssetSwap,
	determineAction,
	getUnprocessedVins,
	isApprovable,
	isConfirmable,
	isDealerTradeBlocked,
	isDeletable,
	isRejectable,
	renderString,
} from "../helpers/pendingTransferRequestHelpers";
import { and } from "@Common/helpers/sonar";
import { getLogger } from "@/Common/helpers/getLogger";
import { buildLinkedRowFilterFn, linkedRowsComparatorFnBuilder } from "@/Common/helpers/Tables/linkedRows";
import { isCurrentPPandCC } from "@/Common/helpers/PayPeriods";
import { createTransferCounterparts } from "../helpers";
import { wrapFilterOperators, wrapQuickFilter } from "@/Common/helpers/mui/filters";
import { OnChangeCallback, SelectionAndFilterCheckbox } from "../SelectionAndFilterCheckbox";
import { SelectionAndFilterCheckboxAll } from "../SelectionAndFilterAllCheckbox";
import BlockedVinMessage from "../Widgets/DealerTrades/BlockedVinMessage";

//Constants
import { DATE_FORMAT } from "@/Constants/date";
import { CELL_CLASS_NAME, CUSTOM_PROPS, SLOTS } from "./commonProps";
import { EActionType, InventoryTab, PENDING_TRANSFERS_TABLE, TRANSFER_ASSET_SWAP } from "../constants";

// Contexts
import { useInventoryContext } from "../contexts/InventoryFilterContext";

// Types
import { ITransferRequestInput, TActionToDo, TDealerInventoryTrade } from "@/DataPages/components/Inventory/types/dealerTrades";

const log = getLogger(PENDING_TRANSFERS_TABLE);

const renderHeaderHandler = (params: GridColumnHeaderParams) => <TypographyHeader fontSize="12px" fontLineHeight="14px" params={params} />;

const isTradeSelectable = (row: TDealerInventoryTrade, currentLocation: string, actionRef: MutableRefObject<TActionToDo>) => {
	const result = determineAction({ requestSelected: row, locationId: currentLocation });

	if (actionRef.current === "") {
		return Boolean(result);
	}
	return result === actionRef.current;
};

type TTransferRequestTableProps = {
	hasEditPermission: boolean;
	locationId: string;
	commissionCycleId?: string;
	payPeriodId: string;
	data?: TDealerInventoryTrade[];
	actionRef: MutableRefObject<TActionToDo>;
	isCurrentPayPeriod: boolean;
};

export const PendingTransferRequestTable = (props: Readonly<TTransferRequestTableProps>) => {
	const { hasEditPermission, locationId, commissionCycleId, payPeriodId, data: rows = [], actionRef, isCurrentPayPeriod } = props;
	const apiRef = useGridApiRef();
	const { enqueueSnackbar } = useSnackbar();

	const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);

	const { locations, isLoading: isLoadingLocations } = useLocations(getTenantParameters(locationId)?.tenant);
	const franchisesByLocations = locations?.map((item) => ({ franchises: item.franchises, locationId: item.locationId })) ?? [];
	const mappedLocations = locations?.reduce<Record<string, string>>((acc, item) => ({ ...acc, [item.locationId]: item.name }), {});

	const { clearFilterSelection, addManyToFilter } = useInventoryContext();
	const { selectedIds, clearSelection } = usePersistentSelection({ table: PENDING_TRANSFERS_TABLE });
	const [rowsSelected, setRowsSelected] = useState<GridRowId[]>(selectedIds);

	const { mutate: updateTransferRequestStatus } = useMutation({
		mutationKey: ["handleUpdateTransferRequestStatus", locationId],
		mutationFn: (trades: ITransferRequestInput[]) => mutateTransferRequestStatus({ trades, locationId }),
		onSuccess: ({ transferRequests }: { transferRequests: TDealerInventoryTrade[] }) => {
			transferRequests.forEach((item) => {
				hideSkeletonWhenLoading(item._id);
			});

			const unprocessedVins = getUnprocessedVins(transferRequests);

			if (unprocessedVins?.length) {
				enqueueSnackbar(<SnackbarsContent component={<BlockedVinMessage unprocessedVins={unprocessedVins} />} />, {
					variant: "warning",
					preventDuplicate: true,
					action: () => {
						const key = uuidv4();
						return <SnackbarsActions key={key} index={key} />;
					},
				});
			}
		},
	});

	const { swapCounterparts, links } = useMemo(() => createTransferCounterparts(rows, locationId), [rows, locationId]);

	const { commissionCycles } = useCommissionCyclesData();

	const selectedPPIsCurrent = useMemo(
		() => isCurrentPPandCC({ commissionCycleId: commissionCycleId ?? "", payPeriodId, commissionCycles }),
		[commissionCycleId, payPeriodId, commissionCycles]
	);

	const handleSelectionChange: OnChangeCallback<TDealerInventoryTrade> = useCallback(
		(checked, id) => {
			setRowsSelected((currentSelected) => {
				log("handleSelectionChange %O", { checked, id, currentSelected });
				const newSelected = checked ? [...currentSelected, id] : currentSelected.filter((item) => item !== id);

				if (selectedPPIsCurrent) {
					const firstSelected = rows.find((item) => item._id === newSelected[0]);

					if (!firstSelected) {
						actionRef.current = "";
					} else {
						const firstAction = determineAction({ requestSelected: firstSelected, locationId });
						actionRef.current = firstAction;
					}
				}

				return newSelected;
			});
		},
		[rows, selectedPPIsCurrent]
	);

	const columns: GridColDef[] = useMemo(() => {
		log("building columns %O", { rows, links, locationId, selectedPPIsCurrent });

		const linkedFilterFn = buildLinkedRowFilterFn<TDealerInventoryTrade>(links);

		const wrappedOperators = wrapFilterOperators(linkedFilterFn);
		const getApplyQuickFilterFn = wrapQuickFilter(linkedFilterFn);
		const columnsBase = [
			{
				field: GRID_CHECKBOX_SELECTION_COL_DEF.field,
				width: 50,
				filterable: false,
				sortable: false,
				resizable: false,
				renderHeader: () => (
					<SelectionAndFilterCheckboxAll
						className="!py-1"
						table={PENDING_TRANSFERS_TABLE}
						data={rows}
						origin={InventoryTab.DEALER_TRADES}
						pathToVinNumber="vehicle.vinNumber"
						disabled={selectedPPIsCurrent}
					/>
				),
				renderCell: (params) => {
					return (
						<SelectionAndFilterCheckbox
							className="!ml-[-11.5px]"
							_id={params.row._id as string}
							table={PENDING_TRANSFERS_TABLE}
							row={params.row as TDealerInventoryTrade}
							origin={InventoryTab.DEALER_TRADES}
							pathToVinNumber="vehicle.vinNumber"
							disabled={selectedPPIsCurrent && !isTradeSelectable(params.row as TDealerInventoryTrade, locationId, actionRef)}
							onChangeCallback={handleSelectionChange}
						/>
					);
				},
			},
			{
				field: "_id",
				headerName: "ID",
				minWidth: 10,
				width: 10,
				filterable: false,
				resizable: false,
			},
			{
				field: "originLocationId",
				flex: 1,
				minWidth: 58,
				headerName: "Origin Store",
				valueGetter: (value: string) => {
					const tenantParameters = getTenantParameters(value);
					const originText = `${tenantParameters?.store}/${tenantParameters?.branch}`;
					return renderString(originText);
				},
				renderCell: (params) => {
					return (
						<TypographyCell
							tooltipProps={{
								title: `Doing Business As: ${mappedLocations?.[params.row.originLocationId]}`,
								placement: "bottom",
							}}
							hasTooltip
							forceTooltip
							params={params}
						/>
					);
				},
			},
			{
				field: "destinationLocationId",
				flex: 1,
				minWidth: 92,
				headerName: "Destination Store",
				valueGetter: (value: string) => {
					const tenantParameters = getTenantParameters(value);
					const destinationText = `${tenantParameters?.store}/${tenantParameters?.branch}`;
					return renderString(destinationText);
				},
				renderCell: (params) => {
					return (
						<TypographyCell
							tooltipProps={{
								title: `Doing Business As: ${mappedLocations?.[params.row.destinationLocationId]}`,
								placement: "bottom",
							}}
							hasTooltip
							forceTooltip
							params={params}
						/>
					);
				},
			},
			{
				field: "type",
				flex: 1,
				minWidth: 49,
				headerName: "Type",
				valueGetter: (_, row) => renderString(row?.vehicle?.type),
			},
			{
				field: "stockNumber",
				headerName: "Stock #",
				width: 69,
				valueGetter: (_, row) => renderString(row?.vehicle?.stockNumber),
			},
			{
				field: "vinNumber",
				width: 80,
				headerName: "VIN",
				valueGetter: (_, row) => renderString(row?.vehicle?.vinNumber),
			},
			{
				field: "make",
				width: 80,
				headerName: "Make",
				valueGetter: (_, row) => renderString(row?.vehicle?.make),
			},
			{
				field: "model",
				width: 80,
				headerName: "Model",
				valueGetter: (_, row) => renderString(row?.vehicle?.model),
			},
			{
				field: "trim",
				width: 95,
				headerName: "Trim",
				valueGetter: (_, row) => renderString(row?.vehicle?.trim),
			},
			{
				field: "colorExterior",
				width: 70,
				headerName: "Ext. Color",
				valueGetter: (_, row) => renderString(row?.vehicle?.colorExterior),
			},
			{
				field: "year",
				width: 55,
				headerName: "Year",
				valueGetter: (_, row) => renderString(row?.vehicle?.year),
			},
			{
				field: "requestedBy",
				flex: 1,
				minWidth: 110,
				headerName: "Requested By",
				valueGetter: (_, row) => row?.requestStatus?.requestedBy,
			},
			{
				field: "approvedBy",
				flex: 1,
				minWidth: 105,
				headerName: "Approved By",
				valueGetter: (_, row) => row?.requestStatus?.approvedBy,
			},
			{
				field: "requestedDate",
				width: 80,
				headerName: "Requested Date",
				valueGetter: (_, row: TDealerInventoryTrade) =>
					getFormatDate(row.requestStatus.requestedDate, DATE_FORMAT.monthAbbrDatePadded),
				valueFormatter: (value) => getFormatDate(value, DATE_FORMAT.monthAbbrDatePadded),
			},
			{
				field: "requestStatus",
				width: 100,
				headerName: "Request Status",
				valueGetter: (_, row: TDealerInventoryTrade) => row.requestStatus.status,
			},
			{
				field: "transferType",
				width: 80,
				headerName: "Transfer Type",
			},
			{
				field: "transferAmount",
				width: 86,
				headerName: "Transfer Amount",
				renderHeader: renderHeaderHandler,
				type: "number",
				align: "right",
				cellClassName: () => CELL_CLASS_NAME,
				valueGetter: (_, row: TDealerInventoryTrade) => row?.vehicle?.transferAmount,
				renderCell: (params) => (
					<TransferAmountCell
						table={"PendingTransferRequest"}
						params={params}
						locationId={locationId}
						commissionCycleId={commissionCycleId}
						payPeriodId={payPeriodId}
						section="pendingRequest"
						apiRef={apiRef}
					/>
				),
			},
		] as GridColDef[];

		return columnsBase.map(
			(colDef) =>
				({
					...colDef,
					cellClassName: colDef.cellClassName ?? (() => CELL_CLASS_NAME),
					renderHeader: colDef.renderHeader ?? renderHeaderHandler,
					renderCell: colDef.renderCell ?? defaultCellRenderer,
					getApplyQuickFilterFn,
					filterOperators: wrappedOperators[colDef.type ?? "string"],
					getSortComparator: (sortDirection) =>
						linkedRowsComparatorFnBuilder(sortDirection, colDef, swapCounterparts, (_v1, _v2, row1) =>
							row1.originLocationId === row1.requestedLocationId ? -1 : 1
						),
				}) as GridColDef
		);
	}, [swapCounterparts, links, locationId, handleSelectionChange, selectedPPIsCurrent, actionRef, rows]);

	const dataEditAssetSwap = actionEditAssetSwap({ rowsSelected, rows, currentLocation: locationId });

	const updateTransferRequestStatusHandler = async ({ status, reason = "" }: { status: string; reason?: string }) => {
		displayAllLoadingSkeletons();

		const dealerInventoryTradesRows = rows.filter((item) => rowsSelected.includes(item._id));

		const usedStatus = status === EActionType.CANCEL_CONFIRMATION ? EActionType.CANCEL : status;

		const trades = dealerInventoryTradesRows.map((row) => {
			const swapId = row.swapId;
			return {
				swapId,
				reason,
				actionType: usedStatus,
				requestId: row.requestId,
			};
		});
		updateTransferRequestStatus(trades);
	};

	function displayAllLoadingSkeletons() {
		rowsSelected.forEach((rowIdItem) => {
			RowFeedback.skeleton(rowIdItem, apiRef)?.display();
		});
	}

	function hideSkeletonWhenLoading(rowIdItem: GridRowId) {
		RowFeedback.skeleton(rowIdItem, apiRef)?.success();
		setRowsSelected([]);
		clearSelection();
	}

	const slotProps = useMemo(() => {
		const isApprovableValue = isApprovable({
			requestSelected: rows.find((item) => item._id === rowsSelected[0]),
			locationId,
		});

		const isBlocked = isDealerTradeBlocked({ requestSelected: rows.find((item) => item._id === rowsSelected[0]), locationId });
		const isRejectableValue = isRejectable({ requestSelected: rows.find((item) => item._id === rowsSelected[0]), locationId });
		const isDeletableValue = isDeletable({ requestSelected: rows.find((item) => item._id === rowsSelected[0]), locationId });
		const isConfirmableValue = isConfirmable({
			requestSelected: rows.find((item) => item._id === rowsSelected[0]),
			locationId,
		});

		return {
			toolbar: {
				filter: {
					data: rows || [],
					hide: selectedPPIsCurrent && rowsSelected?.length,
					disabled: false,
				},
				search: {
					useQuickFilter: true,
					data: rows,
					hide: selectedPPIsCurrent && rowsSelected?.length,
				},
				customAction: {
					hide: !selectedPPIsCurrent || !rowsSelected?.length,
					CustomComponent: ApproveComponent,
					optionsCustomComponent: {
						delete: {
							isBlocked: isBlocked,
							disabled: !hasEditPermission || !isDeletableValue,
							onClick: updateTransferRequestStatusHandler,
						},
						editAssetSwap: {
							hidden: !hasEditPermission || dataEditAssetSwap.hideButton,
							disabled: dataEditAssetSwap.disableButton,
							onClick: () => setIsEditDialogOpen(true),
						},
						approval: {
							disabledActions: {
								approve: !isApprovableValue,
								reject: !isRejectableValue,
							},
							disabled: !hasEditPermission || and(!isApprovableValue, !isRejectableValue),
							onClick: updateTransferRequestStatusHandler,
						},

						confirmation: {
							disabled: !hasEditPermission || !isConfirmableValue,
							onClick: updateTransferRequestStatusHandler,
						},
					},
					amountSelected: rowsSelected.length,
				},
				sxActions: {
					flexDirection: "row-reverse",
				},
			},
			noRowsOverlay: {
				title: "No Inventory Data",
				className: "!text-montserrat",
			},
			noResultsOverlay: {
				title: "No Inventory Data",
				className: "!text-montserrat",
			},
		};
	}, [rows, rowsSelected, dataEditAssetSwap, selectedPPIsCurrent]);

	useEffect(() => {
		log("rows changed useEffect %O", { selectedPPIsCurrent, rowsSelected, rows, actionRef });
		if (!selectedPPIsCurrent || !rowsSelected?.length || !actionRef.current) {
			return;
		}

		const currentAction = actionRef.current;

		const newRowsSelected = generateNewSelection(rowsSelected, rows, locationId, currentAction);

		clearFilterSelection([{ table: PENDING_TRANSFERS_TABLE, commissionCycleId, payPeriodId }]);
		addManyToFilter(
			newRowsSelected.map((item) => ({
				_id: item._id,
				table: PENDING_TRANSFERS_TABLE,
				vinNumber: item.vinNumber,
				origin: InventoryTab.DEALER_TRADES,
			}))
		);
		setRowsSelected(newRowsSelected.map((item) => item._id));
		actionRef.current = newRowsSelected[0]?.actionToDo ?? "";
	}, [rows]);

	useEffect(() => {
		log("selectedPPIsCurrent changed useEffect %O", { selectedPPIsCurrent, selectedIds, rowsSelected });
		if (!selectedPPIsCurrent) {
			return;
		}

		const currentAction = actionRef.current;
		const newRowsSelected = generateNewSelection(rowsSelected, rows, locationId, currentAction);
		clearFilterSelection([{ table: PENDING_TRANSFERS_TABLE, commissionCycleId, payPeriodId }]);
		addManyToFilter(
			newRowsSelected.map((item) => ({
				_id: item._id,
				table: PENDING_TRANSFERS_TABLE,
				vinNumber: item.vinNumber,
				origin: InventoryTab.DEALER_TRADES,
			}))
		);
		setRowsSelected(newRowsSelected.map((item) => item._id));
		actionRef.current = newRowsSelected[0]?.actionToDo ?? "";
	}, [selectedPPIsCurrent]);

	return (
		<>
			<DataSmartTable
				apiRef={apiRef}
				columns={columns}
				rows={rows || []}
				maxRows={10}
				enableCustomScrollbars
				disableColumnMenu
				checkboxSelection
				slots={SLOTS}
				slotProps={slotProps}
				hideFooter
				customProps={CUSTOM_PROPS}
				rowHeight={25}
				autoHeight={false}
				columnVisibilityModel={{ _id: false }}
				disableRowSelectionOnClick
				rowSelectionModel={selectedIds}
				getRowClassName={(params) => {
					const { row } = params as GridRowClassNameParams<TDealerInventoryTrade>;

					if (row.transferType !== TRANSFER_ASSET_SWAP) {
						return "!border-l-[1px] !border-l-[transparent]";
					}
					const extraBorder = row.destinationLocationId === row.requestedByLocationId ? "!border-b-0" : "!border-t-0";
					return `!border-[#ccc] !border-[1px] ${extraBorder}`;
				}}
			/>
			<EditPendingRequestDialog
				locationId={locationId}
				commissionCycleId={commissionCycleId}
				payPeriodId={payPeriodId}
				isOpen={!dataEditAssetSwap.hideButton && isEditDialogOpen}
				onClose={() => setIsEditDialogOpen(false)}
				mappedLocations={mappedLocations}
				itemSelected={dataEditAssetSwap.itemSelected}
				itemToSwap={dataEditAssetSwap.itemToSwap}
				isLoadingLocations={isLoadingLocations}
				franchisesByLocations={franchisesByLocations}
				onSetRowsSelected={() => {}}
				isCurrentPayPeriod={isCurrentPayPeriod}
			/>
		</>
	);
};

function generateNewSelection(rowsSelected: GridRowId[], rows: TDealerInventoryTrade[], locationId: string, currentAction: string) {
	const newRowsItems = rowsSelected
		.map((rowId) => rows.find((item) => item._id === rowId))
		.filter(Boolean)
		.map((item) => {
			const typedItem = item as TDealerInventoryTrade;
			const actionToDo = determineAction({ requestSelected: typedItem, locationId });
			return {
				_id: typedItem._id,
				vinNumber: typedItem.vehicle.vinNumber,
				actionToDo,
			};
		});

	let newRowsSelected = newRowsItems.filter((item) => item.actionToDo === currentAction);

	if (!newRowsSelected.length) {
		const firstAction = newRowsItems[0]?.actionToDo;
		newRowsSelected = newRowsItems.filter((item) => item.actionToDo === firstAction);
	}

	return newRowsSelected;
}
